import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';
import { maybeReloadForStaleChunk } from './chunkReloadRecovery';

/**
 * Heuristic for "this dynamic import failed because the chunk is gone" (the
 * stale-chunk-after-redeploy case) vs any other error. Browsers phrase the
 * failure differently (Chrome/Firefox/Safari), so match the known variants.
 */
const CHUNK_LOAD_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|dynamically imported module/i;

export function isChunkLoadError(err: unknown): boolean {
  const message =
    err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return CHUNK_LOAD_ERROR_RE.test(message);
}

/**
 * Drop-in replacement for `React.lazy` for code-split route chunks. When the
 * import rejects with a stale-chunk error after a redeploy, it
 * triggers a one-shot reload via {@link maybeReloadForStaleChunk} and returns a
 * never-resolving promise so the surrounding `<Suspense>` keeps showing its
 * loading spinner — the user sees a benign spinner for the ~1s the reload
 * takes, NOT the "This section failed to load" error card. A non-chunk error,
 * or a chunk error that recurs after a recent reload (the fresh build still
 * fails), re-throws so the FeatureErrorBoundary surfaces the real problem.
 *
 * The generic mirrors `React.lazy`'s own signature so call sites infer exactly
 * as before.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- match React.lazy's generic constraint verbatim so call-site inference is unchanged
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (isChunkLoadError(err) && maybeReloadForStaleChunk()) {
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}
